import React from 'react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import DownloadIcon from '@mui/icons-material/Download';
import Button from '@mui/material/Button';
import ReactGA from 'react-ga4'
import logo from '../assets/images/finaculator1.png';
import addWatermark from './watermark';
import { useState } from "react";
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';

interface YearlyData {
  year: number;
  totalInvestment: number;
  totalReturns: number;
}

interface PDFGeneratorProps {
  yearlyData: YearlyData[];
  monthlyDeposit: string;
  stepUpPercent: string;
  interestRate: string;
  timePeriod: string;
  isCalculated?:boolean,

}

const PDFGenerator: React.FC<PDFGeneratorProps> = ({
  yearlyData,
  monthlyDeposit,
  stepUpPercent,
  interestRate,
  timePeriod,
  isCalculated
}) => {
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const generatePDF = () => {
    ReactGA.event({ 'category': 'Step-up-SIP PDF ', 'action': 'Step-up-SIP PDF Button', 'label': 'Step-up-SIP PDF' })

    if (!isCalculated) {
      setSnackbarMessage("Please calculate the values before downloading the PDF.");
      setSnackbarOpen(true);
      return;
    }
   

    if ( !monthlyDeposit ||
      !stepUpPercent ||
      !interestRate ||
      !timePeriod ||
      !yearlyData.length ||
      !isCalculated) {
      setSnackbarMessage("Please ensure all fields are filled before downloading the PDF.");
      setSnackbarOpen(true);
      return;
    }
    

    const doc = new jsPDF();
    const titleFontSize = 14;
    const bodyFontSize = 10;
    const investmentSummaryFontSize = 12;

    const watermarkWidth = 100;
    const watermarkHeight = 100;
    const verticalSpacing = 15;

    const title = 'Step up SIP Investment Summary';
    const titleWidth = doc.getStringUnitWidth(title) * titleFontSize / doc.internal.scaleFactor;
    const pageWidth = doc.internal.pageSize.getWidth();
    const titleX = (pageWidth - titleWidth) / 2;

    doc.setFontSize(titleFontSize);
    doc.text(title, titleX, verticalSpacing);

    const inputDataRows = [
      ['Monthly Deposit', `${monthlyDeposit}`],
      ['Annual Step-Up Percentage', `${stepUpPercent}%`],
      ['Interest Rate', `${interestRate}%`],
      ['Time Period', `${timePeriod} years`],
    ];

    const inputTableWidth = 100;
    const inputTableX = (pageWidth - inputTableWidth) / 2;

    autoTable(doc, {
      head: [['Input Details', 'Values']],
      body: inputDataRows,
      startY: verticalSpacing + 20,
      headStyles: { fillColor: [22, 160, 133] },
      theme: 'grid',
      columnStyles: {
        0: { cellWidth: 60 },
        1: { cellWidth: 40 },
      },
      margin: { left: inputTableX },
      styles: { overflow: 'linebreak', cellWidth: 'auto', fontSize: bodyFontSize },
    });

    const yPositionAfterInputTable = doc.autoTable.previous.finalY + 10;

    const tableColumn = ["Year", "Total Investment", "Total Returns"];

    const tableRows = yearlyData.map(data => [
      data.year,
      `${data.totalInvestment.toFixed(2)}`,
      `${data.totalReturns.toFixed(2)}`,
    ]);

    const investmentTableWidth = 120;
    const investmentTableX = (pageWidth - investmentTableWidth) / 2 - 10;

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: yPositionAfterInputTable,
      headStyles: { fillColor: [22, 160, 133] },
      theme: 'grid',
      columnStyles: {
        0: { cellWidth: 50 },
        1: { cellWidth: 50 },
        2: { cellWidth: 50 },
      },
      margin: { left: investmentTableX },
      styles: { overflow: 'linebreak', cellWidth: 'auto', fontSize: investmentSummaryFontSize,halign:'center' },
    });
    addWatermark(doc, logo, watermarkWidth, watermarkHeight);
    const img = new Image();
    img.src = logo;
    img.onload = () => {
      const now = new Date();
      const padZero = (num: string | number) => ("0" + num).slice(-2);
      const formattedDate = `${now.getFullYear()}-${padZero(now.getMonth() + 1)}-${padZero(now.getDate())}`;
      const formattedTime = `${padZero(now.getHours())}-${padZero(now.getMinutes())}-${padZero(now.getSeconds())}`;
      const timestamp = `${formattedDate}_${formattedTime}`;

      const fileName = `finaculator_stepup_sip_calculator_${timestamp}.pdf`;
      doc.save(fileName);
    };
  };

  return (
    <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
      <Button
        variant="contained"
        color="success"
        onClick={generatePDF}
        style={{ marginTop: -70 }}
        startIcon={<DownloadIcon />}
      >
        Download PDF
      </Button>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }} 
      >
        <MuiAlert onClose={handleSnackbarClose} severity="warning" sx={{ width: '100%' }}>
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </div>
  );
};

export default PDFGenerator;
